/**
 * @file BillCostFrequencyDropdown.js
 * @description A dropdown component for selecting a billing cost frequency.
 */

import React, { useState, useEffect } from 'react';
import { fetchBillCostFrequencies } from '../../../services/systemService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';

/**
 * A dropdown component for selecting a billing cost frequency.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label should be displayed inline.
 * @param {number} props.selectedCostFrequency - The ID of the selected bill cost frequency.
 * @param {Function} props.onCostFrequencyChange - The callback function to be called when the selection changes.
 * @param {string} [props.label="Cost Frequency"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @returns {JSX.Element} The rendered BillCostFrequencyDropdown component.
 */
const BillCostFrequencyDropdown = ({
    className,
    inlineLabel = true,
    selectedCostFrequency,
    onCostFrequencyChange,
    label = 'Cost Frequency',
    isRequired = false,
}) => {
    const [costFrequencies, setCostFrequencies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getCostFrequencies = async () => {
            setIsLoading(true);
            try {
                const data = await fetchBillCostFrequencies();
                setCostFrequencies(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Could not fetch bill cost frequencies:', error);
                Toast.error('Unable to load cost frequencies.');
            } finally {
                setIsLoading(false);
            }
        };

        getCostFrequencies();
    }, []);

    const getDefaultLabel = () => {
        const matchingFrequency = costFrequencies.find(
            frequency => frequency.id === selectedCostFrequency,
        );
        return matchingFrequency
            ? matchingFrequency.name
            : 'Select Cost Frequency';
    };

    const costFrequencyOptions = costFrequencies.map(frequency => ({
        value: frequency.id,
        label: frequency.name,
    }));

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="bill-cost-frequency-dropdown">
                {label}
                {isRequired && <span className="text-danger">*</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    id="bill-cost-frequency-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={costFrequencyOptions}
                    value={selectedCostFrequency}
                    onChange={onCostFrequencyChange}
                    disabled={costFrequencies.length === 0 || isLoading}
                />
            </LoadingWrapper>
        </div>
    );
};

export default BillCostFrequencyDropdown;
